import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosError } from 'axios'
import * as qs from 'qs'
import { useAuthStore } from '@/stores/auth'

const getEnvValue = (primaryKey: string, legacyKey: string): string => {
  return (
    (import.meta.env[primaryKey] as string | undefined)?.trim() ||
    (import.meta.env[legacyKey] as string | undefined)?.trim() ||
    ''
  )
}

// Use VITE_API_ENDPOINT if available (like legacy VUE_APP_API_ENDPOINT)
// Otherwise construct from VITE_API_BASE_URL
// In development, ALWAYS use proxy to avoid CORS issues
const getBaseURL = (): string => {
  // In development mode, ALWAYS use proxy to avoid CORS
  if (import.meta.env.DEV) {
    // Use /api/v1 so requests go through Vite proxy configured in vite.config.js
    // The proxy will forward to the backend configured in VITE_API_BASE_URL or VITE_API_ENDPOINT
    return '/api/v1'
  }

  // In production, use environment variables directly
  // If VITE_API_ENDPOINT is set, use it directly (like legacy code)
  const apiEndpoint = getEnvValue('VITE_API_ENDPOINT', 'VUE_APP_API_ENDPOINT')
  if (apiEndpoint) {
    return apiEndpoint
  }

  // If VITE_API_BASE_URL is set, construct the full endpoint
  const apiBaseUrl = getEnvValue('VITE_API_BASE_URL', 'VUE_APP_API_BASE_URL')
  if (apiBaseUrl) {
    const baseUrl = apiBaseUrl
    return baseUrl.endsWith('/api/v1') ? baseUrl : `${baseUrl.replace(/\/$/, '')}/api/v1`
  }

  // Production fallback
  return '/api/v1'
}

const apiClient: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  paramsSerializer: params => {
    return qs.stringify(params, { indices: false })
  },
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false
// Type for queued requests during token refresh
// Parameter names in function types are required by TypeScript but flagged by ESLint
type QueuedRequest = {
  // eslint-disable-next-line no-unused-vars
  resolve: (value?: string) => void
  // eslint-disable-next-line no-unused-vars
  reject: (error?: AxiosError) => void
}
let failedQueue: QueuedRequest[] = []

const processQueue = (error: AxiosError | null, token: string | null = null): void => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token || undefined)
    }
  })

  failedQueue = []
}

// Request interceptor - Add access token from store (memory)
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()

    if (config.skipAuth) {
      if (config.headers && 'Authorization' in config.headers) {
        delete config.headers.Authorization
      }
      return config
    }

    if (config.bearerToken && config.headers) {
      config.headers.Authorization = `Bearer ${config.bearerToken}`
      return config
    }

    if (authStore.accessToken && config.headers) {
      config.headers.Authorization = `Bearer ${authStore.accessToken}`
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle 401 and refresh token
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }
    const authStore = useAuthStore()

    // Handle "Unauthenticated." error (legacy behavior)
    const responseData = error.response?.data
    if (
      error.response?.status === 401 &&
      (responseData === 'Unauthenticated.' ||
        (typeof responseData === 'object' &&
          responseData !== null &&
          'error' in responseData &&
          (responseData as { error?: string }).error === 'Unauthenticated.'))
    ) {
      // Clear auth and redirect to login (legacy behavior)
      authStore.logout()
      return Promise.reject(error)
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            return apiClient(originalRequest)
          })
          .catch(err => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        await authStore.refreshToken()
        const newToken = authStore.accessToken

        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        }

        processQueue(null, newToken)
        isRefreshing = false

        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null)
        isRefreshing = false

        // Only logout if refresh token is invalid (not if it's just missing)
        const refreshTokenValue = localStorage.getItem('customer_refresh_token')
        if (!refreshTokenValue) {
          // No refresh token, just reject
          return Promise.reject(refreshError)
        }

        // Refresh failed, logout and redirect
        authStore.logout()
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors or re-throw
    if (responseData) {
      // Check for Laravel error structure (message field)
      if (
        typeof responseData === 'object' &&
        responseData !== null &&
        'message' in responseData &&
        typeof (responseData as { message?: unknown }).message === 'string'
      ) {
        return Promise.reject(new Error((responseData as { message: string }).message))
      }
      // Check for error object with message
      if (
        typeof responseData === 'object' &&
        responseData !== null &&
        'error' in responseData &&
        typeof (responseData as { error?: { message?: string } }).error === 'object' &&
        (responseData as { error: { message?: string } }).error?.message
      ) {
        return Promise.reject(
          new Error((responseData as { error: { message: string } }).error.message)
        )
      }
      // Check for plain string error
      if (typeof responseData === 'string' && responseData.trim().length > 0) {
        return Promise.reject(new Error(responseData.trim()))
      }
    }

    return Promise.reject(error)
  }
)

export { apiClient }
export default apiClient
